import { useState } from 'react';
import { CreditCardIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useCreateBillComCustomer } from '../../../../hooks/usePracticeDetail';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import { UDField } from '../../users/UDField';
import { UDInput } from '../../users/UDInput';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';

export interface BillComPrefill {
    name: string;
    email: string;
    phone: string;
    address1: string;
    city: string;
    state: string;
    zip: string;
    country: string;
}

interface CreateBillComCustomerModalProps {
    practiceId: string;
    prefill: BillComPrefill;
    onClose: () => void;
}

// '' -> null (an explicit clear the backend should honor), everything else
// trimmed as typed. Never omits a key: every field the user can see and edit
// in this modal is sent explicitly, so a value they blanked out never
// silently falls back to the practice's own stored value server-side.
function toNullableTrim(value: string): string | null {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
}

// name is the one exception - the backend's own `name` schema is
// `min(1).optional()`, so an empty string would fail validation where
// omitting the key lets the backend fall back to "Dr {doctor name}" or the
// practice name (see bill-com.service.ts's customerName fallback).
function toOptionalName(value: string): string | undefined {
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
}

// Sibling to AddPracticeUserModal's New user pane - same overlay/card/UDField
// chrome, forked (not shared) because this form has twice the fields and its
// own submit contract; matching that existing pane's conventions keeps the
// two consistent without adding a shared abstraction for a single reuse.
export function CreateBillComCustomerModal({
    practiceId,
    prefill,
    onClose
}: CreateBillComCustomerModalProps) {
    const { showToast } = useToast();
    const createCustomer = useCreateBillComCustomer(practiceId);
    const [fields, setFields] = useState<BillComPrefill>(prefill);

    useEscapeKey(onClose, !createCustomer.isPending);

    const updateField = <K extends keyof BillComPrefill>(key: K, value: string) => {
        setFields((prev) => ({ ...prev, [key]: value }));
    };

    const handleSubmit = async () => {
        try {
            const result = await createCustomer.mutateAsync({
                name: toOptionalName(fields.name),
                email: toNullableTrim(fields.email),
                phone: toNullableTrim(fields.phone),
                address1: toNullableTrim(fields.address1),
                city: toNullableTrim(fields.city),
                state: toNullableTrim(fields.state),
                zip: toNullableTrim(fields.zip),
                country: toNullableTrim(fields.country)
            });
            showToast(`Bill.com customer created: ${result.billComCustomerId}`, 'success');
            onClose();
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to create Bill.com customer'), 'error');
        }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-bill-com-customer-title"
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm px-8"
        >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[480px] max-h-[85vh] flex flex-col overflow-hidden">
                <div className="px-6 pt-5 pb-4 flex items-start gap-3.5 shrink-0">
                    <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-gray-100 text-gray-500">
                        <CreditCardIcon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                        <h2
                            id="create-bill-com-customer-title"
                            className="text-[17px] font-bold text-gray-900"
                        >
                            Create Bill.com customer
                        </h2>
                        <p className="text-[13px] text-gray-600 leading-relaxed mt-1">
                            Creates a customer record in Bill.com and saves its id back to this
                            practice.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={createCustomer.isPending}
                        className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 shrink-0 disabled:opacity-50"
                        aria-label="Close"
                    >
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                </div>

                <div className="px-6 pb-5 flex flex-col gap-4 overflow-y-auto">
                    <UDField label="Name">
                        <UDInput
                            value={fields.name}
                            onChange={(v) => updateField('name', v)}
                            placeholder="Customer name"
                            autoFocus
                        />
                    </UDField>
                    <UDField label="Email">
                        <UDInput
                            value={fields.email}
                            onChange={(v) => updateField('email', v)}
                            placeholder="billing@practice.com"
                            type="email"
                        />
                    </UDField>
                    <UDField label="Phone">
                        <UDInput
                            value={fields.phone}
                            onChange={(v) => updateField('phone', v)}
                            placeholder="Office phone"
                        />
                    </UDField>
                    <UDField label="Address line 1">
                        <UDInput
                            value={fields.address1}
                            onChange={(v) => updateField('address1', v)}
                            placeholder="Street address"
                        />
                    </UDField>
                    <div className="grid grid-cols-2 gap-3">
                        <UDField label="City">
                            <UDInput
                                value={fields.city}
                                onChange={(v) => updateField('city', v)}
                                placeholder="City"
                            />
                        </UDField>
                        <UDField label="State">
                            <UDInput
                                value={fields.state}
                                onChange={(v) => updateField('state', v)}
                                placeholder="State"
                            />
                        </UDField>
                        <UDField label="ZIP">
                            <UDInput
                                value={fields.zip}
                                onChange={(v) => updateField('zip', v)}
                                placeholder="ZIP"
                            />
                        </UDField>
                        <UDField label="Country">
                            <UDInput
                                value={fields.country}
                                onChange={(v) => updateField('country', v)}
                                placeholder="US"
                            />
                        </UDField>
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2.5 shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={createCustomer.isPending}
                        className="text-sm font-semibold text-gray-500 hover:text-gray-900 px-3 h-10 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        disabled={createCustomer.isPending}
                        onClick={() => void handleSubmit()}
                        className="bg-black text-white font-semibold px-5 h-10 rounded-xl text-sm hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {createCustomer.isPending && (
                            <span className="h-3.5 w-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        )}
                        {createCustomer.isPending ? 'Creating…' : 'Create customer'}
                    </button>
                </div>
            </div>
        </div>
    );
}
